import React, { useState, createContext, useContext, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import { useKeyboard, useFocusTrap } from '$hooks/events';

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

const Context = createContext();

/**
 * @function usePortal
 * @param {object}
 *     @property {string} parentSelector
 *         The selector of the parent node in the DOM tree. It will be used
 *         as a root for the portal.
 *     @method render
 *         Should return the React tree to insert into the targeted portal root.
 *
 * Generates a portal with a root node in the DOM. Useful for components
 * Follow the documentation for more information: https://reactjs.org/docs/portals.html
 * that need to break out of their parents such as dialogs, tooltips, etc.
 * 1. When the component is rendered in the browser, prepares the dependencies.
 * 2. Once the dependencies are ready (after mount), append the temporary node
 *    to the parent root. When the component is unmounted, the temporary node
 *    is discarded.
 * 3. If the dependencies are not ready, delays the rendering.
 * 4. Once everything is ready, returns the portal instance.
 */
const usePortal = ({ parentSelector, render }) => {
    const [ portalParentElement, setPortalParentElement ] = useState(null);
    const [ portalElement, setPortalElement ] = useState(null);

    /* [1] */
    useEffect(() => {
        setPortalParentElement(document.querySelector(parentSelector));
        setPortalElement(document.createElement('div'));
    }, []);

    /* [2] */
    useEffect(() => {
        if (!portalParentElement || !portalElement) {
            return;
        }

        portalParentElement.appendChild(portalElement);
        return () => portalParentElement.removeChild(portalElement);
    }, [ portalParentElement, portalElement ]);

    /* [3] */
    if (!portalParentElement || !portalElement) {
        return null;
    }

    /* [4] */
    return ReactDOM.createPortal(
        render(),
        portalElement
    );
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component ContextProvider
 * @props (see propTypes)
 * @example:
 *     <ContextProvider {...}>
 *         <... />
 *         <Dialog {...}>
 *             <... />
 *         </Dialog>
 *     </ContextProvider>
 *
 * The context provider enables the implementation of a state-based logic
 * in the parents of the modal component. If it is not explicitly provided
 * at the parent level, the dialog component will call it itself.
 */
const ContextProvider = ({
    children,
    id,
    isOpenedInitial = false,
    shouldToggleVisibility = true,
    toggleDelay = 0
}) => {
    const [ isOpened, setIsOpened ] = useState(isOpenedInitial);
    const ids = { module: id };

    const context = {
        ids,
        isOpened,
        setIsOpened,
        shouldToggleVisibility,
        toggleDelay
    };

    return (
        <Context.Provider value={context}>
            {children}
        </Context.Provider>
    );
};

/**
 * @component Dialog
 * @props (see propTypes)
 * @example:
 *     <Dialog {...}>
 *         <CloseButton>...</CloseButton>
 *     </Dialog>
 *
 * Generates a dialog element that comes on top of the page and blocks the scroll.
 * The module implements the following WCAG recommendations: https://www.w3.org/TR/wai-aria-practices-1.1/#dialog_modal
 * 1. Manages the keyboard manipulations.
 * 2. Manages the focus position based on the opening state:
 * 2.1. If the dialog opens, the focus state is put on the container.
 * 2.2. If the dialog closes, the focus state is put back to the element
 *      it was set on previously.
 * 3. Dispatches the appropriate event based on the current state.
 */
const Dialog = props => {
    const context = useContext(Context);
    const { isOpened, setIsOpened, shouldToggleVisibility, toggleDelay, ids } = context;
    const { elementRef } = useFocusTrap(isOpened);
    const lastFocusedElementRef = useRef(null);

    /* [1] */
    useKeyboard({
        escape: () => setIsOpened(false)
    });

    /* [2] */
    useEffect(() => {
        setTimeout(() => {
            if (isOpened && elementRef.current) { /* [2.1] */
                lastFocusedElementRef.current = document.activeElement;
                elementRef.current.focus({ preventScroll: true });
            } else { /* [2.2] */
                lastFocusedElementRef.current?.focus({ preventScroll: true });
                lastFocusedElementRef.current = null;
            }
        }, toggleDelay);
    }, [ isOpened ]);

    return usePortal({
        parentSelector: '#dialogs',
        render: () => (
            <div
                aria-hidden={isOpened ? 'false' : 'true'}
                aria-modal='true'
                className={props.className}
                hidden={!isOpened && shouldToggleVisibility}
                id={ids.module}
                ref={elementRef}
                role='dialog'
                tabIndex='-1'
                {...props} />
        )
    });
};

/**
 * @component OpenButton
 * @props (see propTypes)
 *
 * Represents the close button within the dialog.
 */
const OpenButton = props => {
    const { isOpened, setIsOpened, ids } = useContext(Context);

    return (
        <button
            aria-controls={ids.module}
            aria-expanded={isOpened ? 'true' : 'false'}
            onClick={() => setIsOpened(true)}
            type='button'
            {...props} />
    );
};

/**
 * @component CloseButton
 * @props (see propTypes)
 *
 * Represents the close button within the dialog.
 */
const CloseButton = props => {
    const { isOpened, setIsOpened, ids } = useContext(Context);

    return (
        <button
            aria-controls={ids.module}
            aria-expanded={isOpened ? 'true' : 'false'}
            onClick={() => setIsOpened(false)}
            type='button'
            {...props} />
    );
};

/* ========================================================================= */
/* Typing & export(s) */
/* ========================================================================= */

ContextProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ]),
    id: PropTypes.string.isRequired,
    isOpenedInitial: PropTypes.bool,
    shouldToggleVisibility: PropTypes.bool,
    toggleDelay: PropTypes.number
};

Dialog.propTypes = {
    ariaLabel: PropTypes.string,
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array
    ]),
    className: PropTypes.string
};

OpenButton.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array
    ]),
    className: PropTypes.string
};

CloseButton.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array
    ]),
    className: PropTypes.string
};

const DialogCloseButton = CloseButton;
const DialogContext = Context;
const DialogContextConsumer = Context.Consumer;
const DialogContextProvider = ContextProvider;
const DialogOpenButton = OpenButton;

export {
    Dialog,
    DialogCloseButton,
    DialogContext,
    DialogContextConsumer,
    DialogContextProvider,
    DialogOpenButton
};
